import {
  createContext,
  ReactElement,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';
import axios from 'axios';
import { notify } from '@lib/bugsnag';
import { NotifiableError } from '@bugsnag/core';

export type GasPrice = Omit<OracleGetGasPriceResult, 'blockNum'>;

export interface GetGasPriceResult {
  data: GasPrice | null;
  ok: boolean;
}

export type Quote = OracleGetQuoteResult;

export interface GetQuoteResult {
  data: Quote | null;
  ok: boolean;
}

export interface OracleApiResponseBody<T> {
  result: T;
  status: string;
}

export interface OracleGetGasPriceResult {
  average: number;
  blockNum: number;
  fast: number;
  fastest: number;
  safeLow: number;
}

export interface OracleGetQuoteResult {
  aggregatedPrice: number;
  // oracleAddress: string;
  // roundId: string;
  // settled: boolean;
  timestamp: string;
  tokenPairDecimal: number;
  tokenPairName: string;
}

export interface OracleContextValue {
  getGasPrice(): Promise<GetGasPriceResult>;
  getQuote(
    baseTokenSymbol: string,
    quoteTokenSymbol: string
  ): Promise<GetQuoteResult>;
}

export const OracleContext = createContext<OracleContextValue>({
  getGasPrice: async (): Promise<GetGasPriceResult> => ({
    data: null,
    ok: false,
  }),
  getQuote: async (): Promise<GetQuoteResult> => ({ data: null, ok: false }),
});

interface OracleProviderProps {
  baseUrl: string;
  children: ReactNode;
}

function OracleProvider({
  baseUrl: BASE_URL,
  children,
}: OracleProviderProps): ReactElement {
  const getGasPrice = useCallback(async (): Promise<GetGasPriceResult> => {
    if (!BASE_URL) {
      return { data: null, ok: false };
    }

    try {
      const { data } = await axios.get<
        OracleApiResponseBody<OracleGetGasPriceResult>
      >(`${BASE_URL}/gasprice`);

      if (data.status === 'ok') {
        return {
          data: {
            average: data.result.average,
            fast: data.result.fast,
            fastest: data.result.fastest,
            safeLow: data.result.safeLow,
          },
          ok: true,
        };
      }

      return { data: null, ok: false };
    } catch (error) {
      notify(error as NotifiableError);
      return { data: null, ok: false };
    }
  }, [BASE_URL]);
  const getQuote = useCallback(
    async (
      baseTokenSymbol: string,
      quoteTokenSymbol: string
    ): Promise<GetQuoteResult> => {
      function formatAssetPair(): string {
        return `${baseTokenSymbol.toUpperCase()}-${quoteTokenSymbol.toUpperCase()}`;
      }

      if (!BASE_URL) {
        return { data: null, ok: false };
      }

      try {
        const { data } = await axios.get<
          OracleApiResponseBody<OracleGetQuoteResult>
        >(`${BASE_URL}/query/${formatAssetPair()}`);

        if (data.status === 'ok') {
          return { data: data.result, ok: true };
        }

        return { data: null, ok: false };
      } catch (error) {
        notify(error as NotifiableError);
        return { data: null, ok: false };
      }
    },
    [BASE_URL]
  );
  const contextValue: OracleContextValue = useMemo(
    () => ({ getGasPrice, getQuote }),
    [getGasPrice, getQuote]
  );

  return (
    <OracleContext.Provider value={contextValue}>
      {children}
    </OracleContext.Provider>
  );
}

export default OracleProvider;
